/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import { PaginatedTable } from '@ink-ai/portal/common/components/paginated-table/PaginatedTable';
import { useAsyncData } from '@ink-ai/portal/common/hooks/useAsyncData';
import { getApi, makeRequest } from '@ink-ai/portal/common/requestHelper';
import {
  Box,
  Breadcrumbs,
  Button,
  Container,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  IconButton,
} from '@mui/material';
import JSZip from 'jszip';
import {
  DocumentTranslationApi,
  DocumentTranslationResponseItemDtoStatusEnum,
} from '@ink-ai/insight-service-sdk';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { EllipsisCell } from '@ink-ai/portal/common/components/paginated-table/EllipsisCell';
import { formatDate } from '@ink-ai/portal/common/utils';
import {
  handleDownload,
  languageOptions,
  statusClassMapping,
  statusStringMapping,
} from '@ink-ai/portal/reducers/translation';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import RefreshIcon from '@mui/icons-material/Refresh';
import { debounce } from 'lodash-es';
import { useDispatch, useSelector } from 'react-redux';
import { app } from '@ink-ai/portal/reducers/app';
import { RootState } from '@ink-ai/portal/reducers';
import { WsMessageType } from '@ink-ai/portal/common/socket/SocketHandler';
import LoadingButton from '@mui/lab/LoadingButton';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import { PaperCard } from '../components/Common';
import { DeleteOutlineOutlined, FileDownloadOutlined, SendOutlined, VisibilityOutlined } from '@mui/icons-material';
import { hasAnyRole, adminRolesForEnv, submissionRolesForEnv } from '@ink-ai/portal/common/authHelper';

export const statusIconMapping: Record<
  DocumentTranslationResponseItemDtoStatusEnum,
  React.ReactNode
> = {
  [DocumentTranslationResponseItemDtoStatusEnum.PendingUpload]: (
    <PendingOutlinedIcon />
  ),
  [DocumentTranslationResponseItemDtoStatusEnum.Translating]: (
    <PendingOutlinedIcon />
  ),
  [DocumentTranslationResponseItemDtoStatusEnum.Queueing]: (
    <PendingOutlinedIcon />
  ),
  [DocumentTranslationResponseItemDtoStatusEnum.Success]: (
    <CheckCircleOutlineOutlinedIcon />
  ),
  [DocumentTranslationResponseItemDtoStatusEnum.Error]: (
    <ErrorOutlineOutlinedIcon />
  ),
};

export const TranslateDocumentTaskList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const [page, setPage] = useState(0);
  const [downloadId, setDownloadId] = useState('');
  const [approveId, setApproveId] = useState('');
  // Collabora text-merge state per task, probed lazily when the download tooltip
  // opens, so the tooltip can tell the user whether the download will be the clean
  // (optimized) version or is still being prepared. Empty = not probed yet.
  const [mergeStates, setMergeStates] = useState<Record<string, string>>({});
  const [previewId, setPreviewId] = useState('');
  const [menuLoading, setMenuLoading] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState(0);
  const [tempSearch, setTempSearch] = useState('');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [newTranslationAnchor, setNewTranslationAnchor] = React.useState<null | HTMLElement>(null);
  const actionsButtonRef = useRef<HTMLButtonElement>(null);
  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const webSocketState = useSelector((state: RootState) => state.webSocket);
  const authState = useSelector((state: RootState) => state.auth);
  const isAdminOrSubmission = hasAnyRole(authState.group, [...adminRolesForEnv(), ...submissionRolesForEnv()]);

  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const { data, isLoadingData, reloadData } = useAsyncData(async () => {
    const translationApi = await getApi(DocumentTranslationApi);
    return translationApi.queryDocumentTranslation({
      pagination: {
        take: rowsPerPage,
        skip: page * rowsPerPage,
      } as any,
      search,
    });
  }, [page, rowsPerPage, search]);

  useEffect(() => {
    if (lastSyncAt === webSocketState.lastMessageAt) {
      // already in syncing
      return;
    }
    setLastSyncAt(webSocketState.lastMessageAt);
    if (isLoadingData) {
      return;
    }
    if (
      webSocketState.lastMessage?.type !==
      WsMessageType.DOCUMENT_TRANSLATION_RESULT
    ) {
      return;
    }
    const updatedId = webSocketState.lastMessage?.payload.uuid;

    // reload data if current data contains task that to be updated;
    if (
      data.data.data.some(
        ({ id, status }) =>
          id === updatedId &&
          (status === 'TRANSLATING' || status === 'QUEUEING'),
      )
    ) {
      reloadData();
    }
  }, [
    webSocketState.lastMessage,
    webSocketState.lastMessageAt,
    data,
    reloadData,
    isLoadingData,
    lastSyncAt,
  ]);

  const deleteTasks = async (ids: string[]) => {
    if (!ids || ids.length === 0) {
      return;
    }
    const translationApi = await getApi(DocumentTranslationApi);
    for (const id of ids) {
      await translationApi.deleteDocumentTranslation(id);
    }
  };

  // 自动清理：发现列表中存在 PendingUpload（等待上传）的任务，立即删除以避免僵尸任务
  useEffect(() => {
    const items = data?.data?.data ?? [];
    const pendingIds = items
      .filter((it) => it.status === DocumentTranslationResponseItemDtoStatusEnum.PendingUpload)
      .map((it) => it.id);
    if (pendingIds.length > 0) {
      (async () => {
        try {
          await deleteTasks(pendingIds);
        } catch (e) {
          console.error('Auto delete pending upload tasks failed:', e);
        } finally {
          reloadData();
        }
      })();
    }
  }, [data, reloadData]);

  const handleDelete = async () => {
    const ids = pendingDeleteIds.length > 0 ? pendingDeleteIds : selectedIds;
    if (!ids || ids.length === 0) return;
    setMenuLoading(true);
    try {
      await deleteTasks(ids);
    } catch (error) {
      console.error(error);
    } finally {
      setMenuLoading(false);
      setConfirmDeleteOpen(false);
      setPendingDeleteIds([]);
    }
    handleMenuClose();
    reloadData();
    setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
  };

  // SUBMISSION only: sending the result to the external caller's callback URL is a
  // deliberate human step, so nothing is pushed until a reviewer approves here.
  // Re-approving re-sends on purpose — the receiving side may have missed it.
  const handleTaskApprove = async (id: string, name?: string) => {
    setApproveId(id);
    try {
      await makeRequest({
        method: 'POST',
        url: `/api/v1/document/translation/${id}/approve`,
      });
      dispatch(
        app.actions.setGlobalMessage({
          message: t('translateTasks.approve.success'),
          status: 'success',
        }),
      );
      reloadData();
    } catch (error) {
      console.error(error);
      dispatch(
        app.actions.setGlobalMessage({
          message: t('translateTasks.approve.failed', { name: name ?? id }),
          status: 'error',
        }),
      );
    } finally {
      setApproveId('');
    }
  };

  const handleTaskMenuDownload = async () => {
    setMenuLoading(true);
    try {
      if (selectedIds.length === 1) {
        // 暂停自定义文件名与后缀，直接使用后端提供的文件名
        const task = taskById(selectedIds[0]);
        await handleDownload(selectedIds[0], undefined, downloadVariantOf(task));
      } else {
        // 多选下载：打包为 ZIP 后一次下载，避免浏览器拦截多文件自动下载
        const zip = new JSZip();
        const translationApi = await getApi(DocumentTranslationApi);
        const usedNames = new Set<string>();

        const sanitizeFileName = (name: string) =>
          name
            .replace(/[\\/:*?"<>|]/g, '')
            .replace(/[\r\n\t]/g, '')
            .trim();

        const extractNameFromContentDisposition = (cd: string) => {
          if (!cd) return '';
          // Prefer RFC 5987 filename*
          const star = cd.match(/filename\*\s*=\s*([^;]+)/i);
          if (star && star[1]) {
            let v = star[1].trim();
            v = v.replace(/^"|"$/g, '');
            v = v.replace(/^UTF-8''/i, '');
            try {
              return decodeURIComponent(v);
            } catch (_) {
              return v;
            }
          }
          // Fallback to filename="..."
          const norm = cd.match(/filename\s*=\s*"?([^";]+)"?/i);
          if (norm && norm[1]) {
            return norm[1].trim();
          }
          return '';
        };

        const extractNameFromUrl = (url: string) => {
          try {
            const u = new URL(url);
            // 优先从查询参数 response-content-disposition 获取文件名（S3 预签名链接常见写法）
            const cdParam = u.searchParams.get('response-content-disposition') || '';
            if (cdParam) {
              const nameFromParam = extractNameFromContentDisposition(cdParam);
              if (nameFromParam) return nameFromParam;
            }
            const path = u.pathname;
            const last = path.substring(path.lastIndexOf('/') + 1);
            return decodeURIComponent(last);
          } catch (_) {
            return '';
          }
        };

        // 基于响应的 MIME 在缺失扩展名时进行填充
        const mimeToExt: Record<string, string> = {
          'application/pdf': 'pdf',
          'application/msword': 'doc',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
          'application/vnd.ms-excel': 'xls',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
          'text/plain': 'txt',
          'application/json': 'json',
          'application/zip': 'zip',
          'application/octet-stream': 'bin',
        };
        const ensureExtIfMissing = (name: string, mime: string) => {
          const baseMime = (mime || '').split(';')[0].trim();
          const ext = mimeToExt[baseMime];
          if (!name) return name;
          const dot = name.lastIndexOf('.');
          if (dot === -1 && ext) return `${name}.${ext}`;
          return name;
        };

        // 暂停前端自定义扩展名逻辑，使用后端/链接返回的原始文件名

        const makeUnique = (name: string) => {
          if (!usedNames.has(name)) {
            usedNames.add(name);
            return name;
          }
          const dot = name.lastIndexOf('.');
          const hasExt = dot > 0;
          const base = hasExt ? name.substring(0, dot) : name;
          const ext = hasExt ? name.substring(dot) : '';
          let i = 1;
          let candidate = `${base} (${i})${ext}`;
          while (usedNames.has(candidate)) {
            i += 1;
            candidate = `${base} (${i})${ext}`;
          }
          usedNames.add(candidate);
          return candidate;
        };

        for (const id of selectedIds) {
          try {
            // `which` goes through axios `params`: the generated SDK method was not
            // regenerated after the query parameter was added, so it has no argument
            // for it.
            const { data } = await translationApi.getTranslationDownloadLink(id, {
              params: { which: downloadVariantOf(taskById(id)) },
            });
            const url = data.downloadLink;
            const resp = await fetch(url);
            const blob = await resp.blob();

            const listNameRaw = selectedTasks.find((item) => item.id === id)?.name || '';
            const listName = sanitizeFileName(listNameRaw);
            const urlObj = new URL(url);
            const cd = resp.headers.get('content-disposition') || urlObj.searchParams.get('response-content-disposition') || '';
            const ct = resp.headers.get('content-type') || urlObj.searchParams.get('response-content-type') || '';
            const serverName = extractNameFromContentDisposition(cd) || extractNameFromUrl(url) || id;
            let finalName = sanitizeFileName(serverName || listName || id);
            // 若缺失扩展名，依据 MIME 补充扩展名；不更改已有扩展名
            finalName = ensureExtIfMissing(finalName, ct);
            finalName = makeUnique(finalName);

            zip.file(finalName, blob);
          } catch (e) {
            console.error('Failed to add translation file to zip:', e);
          }
        }

        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const a = document.createElement('a');
        const objectUrl = URL.createObjectURL(zipBlob);
        a.href = objectUrl;
        a.download = 'translations.zip';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(objectUrl);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setMenuLoading(false);
      handleMenuClose();
    }
  };

  // Lazy probe (on tooltip hover) of the derived Collabora merge state for a task.
  const probeMergeState = async (id: string) => {
    try {
      const res = await makeRequest<{ state?: string }>({
        method: 'GET',
        url: `/api/v1/document/translation/${id}/merge-state`,
        responseType: 'json',
      });
      setMergeStates((m) => ({ ...m, [id]: res?.state ?? 'none' }));
    } catch {
      /* ignore probe errors — tooltip just shows the default download hint */
    }
  };

  // SUBMISSION docs are reviewed in the Collabora editor, so their download has to
  // serve the TOC-fixed Collabora save; STANDARD gets the merged draft. Derived in
  // one place because the three download entry points had drifted: the row button
  // picked the variant by mode, while the single-select menu and the ZIP path
  // always took the default and so handed out 'merged' for SUBMISSION too.
  const downloadVariantOf = (task: any): 'merged' | 'collabora' =>
    task?.translationMode === 'SUBMISSION' ? 'collabora' : 'merged';

  // `translationMode` / `approvedAt` are not on the generated SDK's response type
  // yet (regenerating it needs a local server to dump the OpenAPI spec), so read
  // them through here rather than casting at every use site.
  const approvalOf = (task: any): { isSubmission: boolean; approvedAt?: number } => ({
    isSubmission: task?.translationMode === 'SUBMISSION',
    approvedAt: task?.approvedAt ?? undefined,
  });

  const taskById = (id: string) =>
    (data?.data.data ?? []).find((item) => item.id === id);

  const handleTaskDownload = async (
    id: string,
    name?: string,
    which: 'merged' | 'collabora' = 'merged',
  ) => {
    setDownloadId(id);
    try {
      // 暂停自定义文件名与后缀，直接使用后端提供的文件名
      await handleDownload(id, undefined, which);
    } catch (error) {
      console.error(error);
    } finally {
      setDownloadId('');
    }
  };


  const debouncedSetSearch = useCallback(debounce(setSearch, 500), [setSearch]);

  useEffect(() => {
    debouncedSetSearch(tempSearch);
  }, [tempSearch, debouncedSetSearch]);

  const selectedTasks = useMemo(() => {
    if (!selectedIds || selectedIds.length === 0) {
      return [];
    }
    return (
      data?.data.data.filter((item) => selectedIds.includes(item.id)) ?? []
    );
  }, [selectedIds, data]);

  const pendingDeleteNames = useMemo(() => {
    if (!pendingDeleteIds || pendingDeleteIds.length === 0) return [] as string[];
    return (
      data?.data.data
        .filter((item) => pendingDeleteIds.includes(item.id))
        .map((item) => item.name) ?? []
    );
  }, [pendingDeleteIds, data]);

  return (
    <Container maxWidth="lg" sx={{ mt: 1, mb: 1 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
        <Link to="/">{t('translateTasks.navigation.home')}</Link>
        <Typography color="textPrimary">
          {t('translateTasks.navigation.translateTasks')}
        </Typography>
      </Breadcrumbs>
      <PaperCard>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
          <TextField
            label={t('translateTasks.search.label')}
            variant="outlined"
            size="small"
            value={tempSearch}
            InputLabelProps={{
              shrink: true,
            }}
            placeholder={t('translateTasks.search.placeholder')}
            onChange={(e) => {
              setTempSearch(e.target.value);
              setPage(0);
            }}
          />
          <Box>
            <Button
              sx={{ mr: 1 }}
              aria-label="refresh"
              variant="outlined"
              onClick={reloadData}
            >
              <RefreshIcon />
            </Button>
            <LoadingButton
              ref={actionsButtonRef}
              onClick={handleMenuOpen}
              endIcon={<ArrowDropDownIcon />}
              variant="outlined"
              disabled={selectedIds.length === 0 || menuLoading}
              loading={menuLoading}
            >
              {t('translateTasks.actions.button')}
            </LoadingButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              slotProps={{
                paper: {
                  style: {
                    minWidth: actionsButtonRef.current?.offsetWidth,
                  },
                },
              }}
            >
              <MenuItem
                onClick={handleTaskMenuDownload}
                disabled={
                  selectedIds.length === 0 ||
                  menuLoading ||
                  selectedTasks.length === 0 ||
                  selectedTasks.some(
                    (task) =>
                      task.status !==
                      DocumentTranslationResponseItemDtoStatusEnum.Success,
                  )
                }
              >
                <Typography>{t('translateTasks.actions.download')}</Typography>
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => {
                  if (selectedIds.length === 0 || menuLoading) return;
                  setPendingDeleteIds(selectedIds);
                  setConfirmDeleteOpen(true);
                }}
                disabled={selectedIds.length === 0 || menuLoading}
              >
                <Typography color="error">
                  {t('translateTasks.actions.delete')}
                </Typography>
              </MenuItem>
            </Menu>
            <Button
              variant="contained"
              color="primary"
              sx={{ ml: 1 }}
              onClick={(e) =>
                isAdminOrSubmission
                  ? setNewTranslationAnchor(e.currentTarget)
                  : navigate('/document-translator?mode=STANDARD')
              }
              endIcon={isAdminOrSubmission ? <ArrowDropDownIcon /> : undefined}
            >
              {t('translateTasks.actions.new')}
            </Button>
            {isAdminOrSubmission && (
              <Menu anchorEl={newTranslationAnchor} open={Boolean(newTranslationAnchor)} onClose={() => setNewTranslationAnchor(null)}>
                <MenuItem component={Link} to="/document-translator?mode=STANDARD" onClick={() => setNewTranslationAnchor(null)}>{t('translation.translationMode.standard')}</MenuItem>
                <MenuItem component={Link} to="/document-translator?mode=SUBMISSION" onClick={() => setNewTranslationAnchor(null)}>{t('translation.translationMode.submission')}</MenuItem>
              </Menu>
            )}
          </Box>
        </Box>
        <PaginatedTable
          singleSelection={false}
          totalCount={data?.data.metadata.totalCount}
          columns={[
            {
              id: 'name',
              label: t('translateTasks.table.filename'),
              render: ({ name }) => <EllipsisCell content={name} maxLines={3} />,
              width: 200,
            },
            {
              id: 'source',
              label: t('translateTasks.table.sourceLanguage'),
              render: ({ sourceLang }) => t(languageOptions.get(sourceLang)),
            },
            {
              id: 'target',
              label: t('translateTasks.table.targetLanguage'),
              render: ({ targetLang }) => t(languageOptions.get(targetLang)),
            },
            ...(isAdminOrSubmission ? [{
              id: 'translationMode',
              label: t('translateTasks.table.mode'),
              render: ({ translationMode }: any) => (
                <Typography variant="body2" sx={{ fontSize: 12 }}>
                  {translationMode === 'SUBMISSION'
                    ? t('translation.translationMode.submission')
                    : t('translation.translationMode.standard')}
                </Typography>
              ),
            }] : []),
            {
              id: 'createdAt',
              label: t('translateTasks.table.createdAt'),
              render: ({ createdAt }) => formatDate(createdAt),
            },
            {
              id: 'updatedAt',
              label: t('translateTasks.table.updatedAt'),
              render: ({ updatedAt }) => formatDate(updatedAt),
            },
            {
              id: 'status',
              label: t('translateTasks.table.status'),
              width: 150,
              render: ({ status, errorMessage, id }) =>
                // status === 'SUCCESS' ? (
                //   <LoadingButton
                //     size="small"
                //     onClick={() => handleTaskDownload(id)}
                //     startIcon={<DownloadIcon />}
                //     variant="outlined"
                //     disabled={id === downloadId}
                //     loading={id === downloadId}
                //   >
                //     {t('translateTasks.actions.download')}
                //   </LoadingButton>
                // ) : (
                  <Tooltip title={errorMessage}>
                    <Typography
                      variant="body2"
                      className={statusClassMapping[status]}
                    >
                      {statusIconMapping[status]}
                      <span className="align-middle ml-1">
                        {t(statusStringMapping[status])}
                      </span>
                    </Typography>
                  </Tooltip>
                // ),
            },
            {
              id: 'actions',
              label: t('table.actions'),
              // Room for a 4th icon (approve) on SUBMISSION rows.
              width: 220,
              render: (record) => (
                // PaginatedTable applies the column width as `maxWidth`, so a
                // narrow viewport squeezes this cell and the icons wrap onto a
                // second line. Keep them on one row explicitly.
                <Box
                  sx={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center' }}
                >
                  <Tooltip
                    placement="top"
                    onOpen={() => probeMergeState(record.id)}
                    title={
                      mergeStates[record.id] === 'pending'
                        ? t('table.tooltip.downloadOptimizing')
                        : mergeStates[record.id] === 'ready'
                          ? t('table.tooltip.downloadOptimized')
                          : t('table.tooltip.download')
                    }
                  >
                    <IconButton
                      color="primary"
                      onClick={() =>
                        handleTaskDownload(
                          record.id,
                          record.name,
                          downloadVariantOf(record),
                        )
                      }
                      onMouseDown={(e) => e.stopPropagation()}
                      disabled={
                        record.status !==
                          DocumentTranslationResponseItemDtoStatusEnum.Success ||
                        downloadId === record.id
                      }
                    >
                      <FileDownloadOutlined/>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('table.tooltip.view')} placement="top">
                    <IconButton
                      color="primary"
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (previewId) return;
                        try {
                          setPreviewId(record.id);
                          // 仅传递任务 UUID，进入预览页后由页面自行调用接口获取原文与译文的下载链接
                          const nameParam = record.name ? `?name=${encodeURIComponent(record.name)}` : '';
                          // SUBMISSION XML has no side-by-side preview worth showing; its
                          // view is the text editor, which already shows source and translation.
                          const isSubmissionXml =
                            approvalOf(record).isSubmission && /\.xml$/i.test(record.name || '');
                          navigate(
                            isSubmissionXml
                              ? `/translate-tasks/${record.id}/xml-editor`
                              : `/translate-tasks/${record.id}/preview${nameParam}`,
                          );
                        } catch (err) {
                          console.error('Open preview failed:', err);
                        } finally {
                          setPreviewId('');
                        }
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      disabled={
                        record.status !==
                          DocumentTranslationResponseItemDtoStatusEnum.Success ||
                        previewId === record.id
                      }
                    >
                      <VisibilityOutlined/>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('table.tooltip.delete')} placement="top">
                    <IconButton
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (menuLoading) return;
                        setPendingDeleteIds([record.id]);
                        setConfirmDeleteOpen(true);
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      disabled={menuLoading}
                    >
                      <DeleteOutlineOutlined/>
                    </IconButton>
                  </Tooltip>
                  {/* SUBMISSION only: STANDARD tasks have no callback URL to send
                      to, so the action does not apply to them at all. Last in the
                      group so the three shared icons stay aligned across rows.
                      Stays enabled after approval — re-approving resends. */}
                  {approvalOf(record).isSubmission && (
                    <Tooltip
                      placement="top"
                      title={
                        approvalOf(record).approvedAt
                          ? t('table.tooltip.approveAgain', {
                              time: new Date(
                                approvalOf(record).approvedAt as number,
                              ).toLocaleString(),
                            })
                          : t('table.tooltip.approve')
                      }
                    >
                      <IconButton
                        color={
                          approvalOf(record).approvedAt ? 'success' : 'primary'
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTaskApprove(record.id, record.name);
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        disabled={
                          record.status !==
                            DocumentTranslationResponseItemDtoStatusEnum.Success ||
                          approveId === record.id
                        }
                      >
                        <SendOutlined />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              )
            }
          ]}
          data={data?.data.data ?? []}
          selectedItems={selectedIds}
          page={page}
          rowsPerPage={rowsPerPage}
          loading={isLoadingData}
          getItemId={(item) => item.id}
          onItemClick={(uuid) => {
            setSelectedIds((prev) =>
              prev.includes(uuid)
                ? prev.filter((id) => id !== uuid)
                : [...prev, uuid],
            );
          }}
          onSelectAllClick={(e) => {
            const checked = e.target.checked;
            const pageIds = (data?.data.data ?? []).map((item) => item.id);
            setSelectedIds(checked ? pageIds : []);
          }}
          onPageChange={(_, newPage) => {
            setPage(newPage);
            setSelectedIds([]);
          }}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
            setSelectedIds([]);
          }}
        />
        {/* 删除确认弹窗 */}
        <Dialog
          open={confirmDeleteOpen}
          onClose={() => setConfirmDeleteOpen(false)}
        >
          <DialogTitle>{t('translateTasks.actions.delete')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('translateTasks.confirmDelete.message', { count: pendingDeleteIds.length })}
            </DialogContentText>
            {pendingDeleteNames.length > 0 && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {pendingDeleteNames.join('、')}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDeleteOpen(false)}>
              {t('common.cancel')}
            </Button>
            <LoadingButton
              color="error"
              variant="contained"
              onClick={handleDelete}
              loading={menuLoading}
            >
              {t('translateTasks.actions.delete')}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </PaperCard>
    </Container>
  );
};
